// src/components/Sidebar.js
import React, { memo, useCallback } from 'react';
import { HomeIcon, CogIcon, BellIcon } from '@heroicons/react/24/outline';
import logo from '../assets/icons/pilari_logo.png';
import { useNavigation } from '../context/NavigationContext';
import { Link, useLocation } from 'react-router-dom';

const mainNavigation = [
  { name: 'Dashboard', icon: HomeIcon, href: '/dashboard' },
  { name: 'Notifications', icon: BellIcon, href: '/notifications' },
];

const settingsNavigation = {
  name: 'Settings',
  icon: CogIcon,
  href: '/settings',
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Sidebar = () => {
  const { current, setCurrent } = useNavigation();
  const location = useLocation();

  const handleClick = useCallback(
    (href) => {
      if (current !== href) setCurrent(href);
    },
    [current, setCurrent]
  );

  const isActive = (href) => location.pathname === href;

  return (
    <div className="h-full w-20 bg-gray-900 flex flex-col items-center py-4 relative z-40">
      <div className="flex-shrink-0">
        <img className="h-8 w-auto" src={logo} alt="Workflow" />
      </div>

      {/* Main menu */}
      <div className="mt-5 flex-1 w-full px-2 space-y-1">
        {mainNavigation.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => handleClick(item.href)}
            className={classNames(
              isActive(item.href)
                ? 'bg-gray-800 text-white'
                : 'text-gray-400 hover:bg-gray-700 hover:text-white',
              'group w-full p-3 rounded-md flex flex-col items-center text-xs font-medium relative'
            )}
            aria-current={isActive(item.href) ? 'page' : undefined}
          >
            <item.icon
              className={classNames(
                isActive(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-white',
                'h-6 w-6'
              )}
            />
            {/* Tooltip */}
            <div className="absolute left-12 top-1/2 transform -translate-y-1/2 ml-2 hidden group-hover:block z-50 transition-opacity duration-300 opacity-0 group-hover:opacity-100">
              <div className="bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                {item.name}
                <div className="bg-black absolute left-[-5px] top-1/2 transform -translate-y-1/2 rotate-45 w-2.5 h-2.5" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Settings at bottom */}
      <div className="w-full px-2 mb-2">
        <Link
          to={settingsNavigation.href}
          onClick={() => handleClick(settingsNavigation.href)}
          className={classNames(
            isActive(settingsNavigation.href)
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:bg-gray-700 hover:text-white',
            'group w-full p-3 rounded-md flex flex-col items-center text-xs font-medium relative'
          )}
          aria-current={isActive(settingsNavigation.href) ? 'page' : undefined}
        >
          <settingsNavigation.icon
            className={classNames(
              isActive(settingsNavigation.href) ? 'text-white' : 'text-gray-400 group-hover:text-white',
              'h-6 w-6'
            )}
          />
          {/* Tooltip */}
          <div className="absolute left-12 top-1/2 transform -translate-y-1/2 ml-2 hidden group-hover:block z-50 transition-opacity duration-300 opacity-0 group-hover:opacity-100">
            <div className="bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap">
              {settingsNavigation.name}
              <div className="bg-black absolute left-[-5px] top-1/2 transform -translate-y-1/2 rotate-45 w-2.5 h-2.5" />
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default memo(Sidebar);
